import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import particularesApiService from '../services/ParticularesApiService';
import SearchableSelect from './common/SearchableSelect';
import Alert from './common/Alert';
import './ParticularDisponibilidadCalendar.css';

const DURACION_OPTIONS = Array.from({ length: 32 }, (_, index) => (index + 1) * 15);
const DEFAULT_PROFESOR_COLOR = '#2563eb';
const DEFAULT_FORM = {
  id: '',
  idUsuarioProfesor: '',
  fecha: '',
  horaInicio: '09:00',
  duracionMinutos: 60,
  cupoIlimitado: false,
  cupo: 1,
  alumnoIds: [],
  alumnoIdsOriginales: [],
  notas: ''
};
const CUPO_MAXIMO = 500;

// Aplica los cambios del docente (agregados y quitados respecto de la lista original) sobre los inscriptos actuales,
// igual que el back: así no se pierden las reservas que hicieron los alumnos mientras el formulario estaba abierto.
const applyAlumnosDiff = (actuales, originales, solicitados) => {
  const originalSet = new Set(originales);
  const solicitadoSet = new Set(solicitados);
  const result = actuales.filter((id) => !originalSet.has(id) || solicitadoSet.has(id));
  solicitados.forEach((id) => {
    if (!originalSet.has(id) && !result.includes(id)) result.push(id);
  });
  return result;
};

const getValue = (source, ...keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return undefined;
};

const getId = (item) => String(getValue(item, 'id', 'Id') || '');
const getEmail = (item) => getValue(item, 'email', 'Email') || '';
const getName = (item, fallback = 'Sin nombre') => {
  const nombre = getValue(item, 'nombre', 'Nombre') || '';
  const apellido = getValue(item, 'apellido', 'Apellido') || '';
  const display = getValue(item, 'displayName', 'DisplayName') || '';
  return `${nombre} ${apellido}`.trim() || display || getEmail(item) || fallback;
};
const getBool = (item, lowerKey, upperKey, fallback = false) => getValue(item, lowerKey, upperKey) ?? fallback;
const normalizeColor = (color) => {
  const value = String(color || '').trim();
  return /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value) ? value : DEFAULT_PROFESOR_COLOR;
};
const hexToRgba = (hex, alpha) => {
  const normalized = normalizeColor(hex).replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;
  const numeric = Number.parseInt(value, 16);
  const red = (numeric >> 16) & 255;
  const green = (numeric >> 8) & 255;
  const blue = numeric & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const pad = (value) => String(value).padStart(2, '0');
const toDateKey = (date) => {
  if (!date) return '';
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return String(date).slice(0, 10);
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};
const toTimeKey = (date) => {
  if (!date) return '09:00';
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) {
    const match = String(date).match(/T(\d{2}:\d{2})/);
    return match?.[1] || '09:00';
  }
  return `${pad(value.getHours())}:${pad(value.getMinutes())}`;
};
const formatRangeDate = (date) => toDateKey(date);
const formatTime = (value) => toTimeKey(value);
const formatDuration = (minutes) => {
  const total = Number(minutes) || 0;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours && rest) return `${hours} h ${rest} min`;
  if (hours) return `${hours} h`;
  return `${rest} min`;
};

// Texto de ocupación: "2 de 5 lugares" o "2 inscriptos · sin límite".
const formatCupo = ({ cupo, cantidadAlumnos }) => {
  const inscriptos = Number(cantidadAlumnos) || 0;
  if (cupo === null || cupo === undefined) {
    return `${inscriptos} inscripto${inscriptos === 1 ? '' : 's'} · sin límite`;
  }
  return `${inscriptos} de ${cupo} lugar${Number(cupo) === 1 ? '' : 'es'}`;
};

const normalizeDisponibilidad = (item) => {
  const profesor = getValue(item, 'profesor', 'Profesor') || {};
  const alumnosRaw = getValue(item, 'alumnos', 'Alumnos') || [];
  const alumnos = Array.isArray(alumnosRaw) ? alumnosRaw : [];
  const estado = getValue(item, 'estado', 'Estado') || 'Disponible';
  const cupoRaw = getValue(item, 'cupo', 'Cupo');
  const cupo = cupoRaw === undefined ? null : Number(cupoRaw);
  const cantidadAlumnos = Number(getValue(item, 'cantidadAlumnos', 'CantidadAlumnos') ?? alumnos.length) || 0;
  return {
    id: getId(item),
    idUsuarioProfesor: String(getValue(item, 'idUsuarioProfesor', 'IdUsuarioProfesor') || ''),
    profesor,
    profesorNombre: getName(profesor, 'Profesor'),
    profesorColor: normalizeColor(getValue(profesor, 'colorCalendario', 'ColorCalendario')),
    fechaInicioLocal: getValue(item, 'fechaInicioLocal', 'FechaInicioLocal'),
    fechaFinLocal: getValue(item, 'fechaFinLocal', 'FechaFinLocal'),
    fechaInicioUtc: getValue(item, 'fechaInicioUtc', 'FechaInicioUtc'),
    fechaFinUtc: getValue(item, 'fechaFinUtc', 'FechaFinUtc'),
    duracionMinutos: Number(getValue(item, 'duracionMinutos', 'DuracionMinutos')) || 60,
    estado,
    alumnos,
    alumnoIds: alumnos.map((alumno) => getId(alumno)).filter(Boolean),
    cupo,
    cantidadAlumnos,
    // ocupado: ya tiene alumnos inscriptos (se abre el detalle). completo: no quedan lugares.
    ocupado: alumnos.length > 0 || String(estado).toLowerCase() === 'ocupado',
    completo: cupo !== null && cantidadAlumnos >= cupo,
    notas: getValue(item, 'notas', 'Notas') || '',
    jitsiRoomName: getValue(item, 'jitsiRoomName', 'JitsiRoomName') || '',
    jitsiRoomUrl: getValue(item, 'jitsiRoomUrl', 'JitsiRoomUrl') || '',
    jitsiCreadaUtc: getValue(item, 'jitsiCreadaUtc', 'JitsiCreadaUtc') || ''
  };
};

const toAlumnoOption = (alumno) => ({
  value: getId(alumno),
  label: getName(alumno, 'Alumno'),
  secondary: getEmail(alumno),
  searchText: `${getName(alumno, '')} ${getEmail(alumno)} ${getValue(alumno, 'dni', 'Dni') || ''}`
});

const Modal = ({ title, icon, children, onClose }) => (
  <div className="particular-calendar-modal-overlay" onClick={onClose}>
    <div className="particular-calendar-modal" onClick={(event) => event.stopPropagation()}>
      <div className="particular-calendar-modal-header">
        <div>
          <span className="particular-calendar-modal-kicker">Calendario docente</span>
          <h3><i className={icon} /> {title}</h3>
        </div>
        <button type="button" className="particular-icon-btn" onClick={onClose} title="Cerrar">
          <i className="fas fa-times" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const ParticularDisponibilidadCalendar = ({ contexto, profesores = [], particularName = 'Particular' }) => {
  const [range, setRange] = useState(null);
  const [selectedProfesorId, setSelectedProfesorId] = useState('');
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [disponibilidades, setDisponibilidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAlumnos, setLoadingAlumnos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [alumnoOptions, setAlumnoOptions] = useState([]);
  const [modal, setModal] = useState({ open: false, mode: 'create', form: DEFAULT_FORM });
  const [detailSlot, setDetailSlot] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState('');

  const usuarioActualId = String(getValue(contexto, 'usuarioActualId', 'UsuarioActualId') || '');
  const canManageAll = Boolean(getValue(contexto, 'puedeGestionarDisponibilidadTodos', 'PuedeGestionarDisponibilidadTodos'));

  const profesorOptions = useMemo(() => {
    const rows = profesores
      .filter((profesor) => getBool(profesor, 'activo', 'Activo', true))
      .map((profesor) => ({
        value: getId(profesor),
        label: getName(profesor, 'Profesor'),
        secondary: getEmail(profesor),
        searchText: `${getName(profesor, '')} ${getEmail(profesor)}`
      }))
      .filter((option) => option.value);

    return canManageAll ? rows : rows.filter((option) => option.value === usuarioActualId);
  }, [canManageAll, profesores, usuarioActualId]);

  const defaultProfesorId = useMemo(() => (
    profesorOptions.find((option) => option.value === usuarioActualId)?.value ||
    profesorOptions[0]?.value ||
    ''
  ), [profesorOptions, usuarioActualId]);

  useEffect(() => {
    if (canManageAll) return;
    if (defaultProfesorId && selectedProfesorId !== defaultProfesorId) {
      setSelectedProfesorId(defaultProfesorId);
    }
  }, [canManageAll, defaultProfesorId, selectedProfesorId]);

  const loadDisponibilidades = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    setError('');

    try {
      const response = await particularesApiService.getDisponibilidades({
        from: range.from,
        to: range.to,
        profesorId: selectedProfesorId
      });
      const rows = response?.disponibilidades || response?.Disponibilidades || [];
      const normalized = Array.isArray(rows) ? rows.map(normalizeDisponibilidad) : [];
      setDisponibilidades(normalized);
      return normalized;
    } catch (err) {
      setError(err.message || 'No pudimos cargar la disponibilidad.');
      setDisponibilidades([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, [range, selectedProfesorId]);

  useEffect(() => {
    loadDisponibilidades();
  }, [loadDisponibilidades]);

  const loadAlumnoOptions = useCallback(async (profesorId) => {
    if (!canManageAll && !profesorId) {
      setAlumnoOptions([]);
      return;
    }

    setLoadingAlumnos(true);
    try {
      const response = await particularesApiService.getDisponibilidadAlumnos({
        profesorId: canManageAll ? '' : profesorId
      });
      const rows = response?.alumnos || response?.Alumnos || [];
      const options = Array.isArray(rows)
        ? rows.map(toAlumnoOption).filter((option) => option.value)
        : [];
      setAlumnoOptions(options);
    } catch (err) {
      setAlumnoOptions([]);
      setError(err.message || 'No pudimos cargar los alumnos para el calendario.');
    } finally {
      setLoadingAlumnos(false);
    }
  }, [canManageAll]);

  useEffect(() => {
    if (!modal.open) return;
    loadAlumnoOptions(modal.form.idUsuarioProfesor);
  }, [loadAlumnoOptions, modal.form.idUsuarioProfesor, modal.open]);

  const calendarEvents = useMemo(() => (
    disponibilidades.map((item) => {
      const color = normalizeColor(item.profesorColor);
      const ocupacion = item.cupo === null
        ? (item.cantidadAlumnos ? `${item.cantidadAlumnos} inscriptos` : 'sin límite')
        : `${item.cantidadAlumnos}/${item.cupo}`;
      return {
        id: item.id,
        title: `${formatTime(item.fechaInicioLocal)} · ${item.profesorNombre} · ${ocupacion}`,
        start: item.fechaInicioLocal,
        end: item.fechaFinLocal,
        backgroundColor: color,
        borderColor: color,
        textColor: '#ffffff',
        classNames: item.completo ? ['availability-event-occupied'] : [],
        extendedProps: { raw: item }
      };
    })
  ), [disponibilidades]);

  const selectedDayKey = toDateKey(selectedDay);
  const selectedDayItems = useMemo(() => (
    disponibilidades
      .filter((item) => String(item.fechaInicioLocal || '').slice(0, 10) === selectedDayKey)
      .sort((a, b) => String(a.fechaInicioLocal).localeCompare(String(b.fechaInicioLocal)))
  ), [disponibilidades, selectedDayKey]);

  const openCreateModal = (date = selectedDay, duration = 60) => {
    const start = date instanceof Date ? date : new Date(date);
    const hasTime = !Number.isNaN(start.getTime()) && (start.getHours() !== 0 || start.getMinutes() !== 0);
    const normalizedDuration = Math.max(15, Math.min(480, Math.round((duration || 60) / 15) * 15));
    setModal({
      open: true,
      mode: 'create',
      form: {
        ...DEFAULT_FORM,
        idUsuarioProfesor: canManageAll ? (selectedProfesorId || defaultProfesorId) : defaultProfesorId,
        fecha: toDateKey(start),
        horaInicio: hasTime ? toTimeKey(start) : '09:00',
        duracionMinutos: normalizedDuration,
        cupoIlimitado: false,
        cupo: 1,
        alumnoIds: []
      }
    });
  };

  const openEditModal = (item) => {
    setSelectedDay(new Date(item.fechaInicioLocal));
    setDetailSlot(null);
    setModal({
      open: true,
      mode: 'edit',
      form: {
        id: item.id,
        idUsuarioProfesor: item.idUsuarioProfesor,
        fecha: toDateKey(item.fechaInicioLocal),
        horaInicio: toTimeKey(item.fechaInicioLocal),
        duracionMinutos: item.duracionMinutos,
        cupoIlimitado: item.cupo === null,
        cupo: item.cupo === null ? Math.max(1, item.cantidadAlumnos) : item.cupo,
        alumnoIds: item.alumnoIds || [],
        alumnoIdsOriginales: item.alumnoIds || [],
        notas: item.notas || ''
      }
    });
  };

  const openDetailModal = (item) => {
    setSelectedDay(new Date(item.fechaInicioLocal));
    setDetailSlot(item);
  };

  const closeDetailModal = () => {
    setDetailSlot(null);
  };

  const openJitsiRoom = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const closeModal = () => {
    if (saving) return;
    setConfirmDelete(false);
    setModal({ open: false, mode: 'create', form: DEFAULT_FORM });
  };

  const updateForm = (patch) => {
    setModal((prev) => ({
      ...prev,
      form: { ...prev.form, ...patch }
    }));
  };

  const handleCalendarDates = (info) => {
    setRange({
      from: formatRangeDate(info.start),
      to: formatRangeDate(info.end)
    });
  };

  const handleDateClick = (info) => {
    setSelectedDay(info.date);
    if (info.view?.type !== 'dayGridMonth') {
      openCreateModal(info.date, 60);
    }
  };

  const handleSelect = (info) => {
    const minutes = Math.max(15, Math.round((info.end.getTime() - info.start.getTime()) / 60000));
    setSelectedDay(info.start);
    openCreateModal(info.start, minutes);
    info.view.calendar.unselect();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = modal.form;
    if (!form.idUsuarioProfesor || !form.fecha || !form.horaInicio) {
      setError('Completá profesor, fecha y hora para continuar.');
      return;
    }

    const duration = Number(form.duracionMinutos);
    if (!Number.isFinite(duration) || duration < 15 || duration > 480 || duration % 15 !== 0) {
      setError('La duración debe estar entre 15 minutos y 8 horas, en bloques de 15 minutos.');
      return;
    }

    const alumnoIds = Array.isArray(form.alumnoIds) ? form.alumnoIds : [];
    const cupo = form.cupoIlimitado ? null : Number(form.cupo);
    if (cupo !== null && (!Number.isInteger(cupo) || cupo < 1 || cupo > CUPO_MAXIMO)) {
      setError(`El cupo debe ser un número entre 1 y ${CUPO_MAXIMO}, o sin límite.`);
      return;
    }
    if (cupo !== null && alumnoIds.length > cupo) {
      setError(`Hay ${alumnoIds.length} alumnos inscriptos y el cupo es ${cupo}. Aumentá el cupo o quitá alumnos.`);
      return;
    }

    const payload = {
      idUsuarioProfesor: form.idUsuarioProfesor,
      fecha: form.fecha,
      horaInicio: form.horaInicio,
      duracionMinutos: duration,
      cupo,
      alumnoIds,
      notas: form.notas
    };
    const isEdit = modal.mode === 'edit' && form.id;
    if (isEdit) {
      payload.alumnoIdsOriginales = Array.isArray(form.alumnoIdsOriginales) ? form.alumnoIdsOriginales : [];
    }

    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        const response = await particularesApiService.updateDisponibilidad(form.id, payload);
        const conservadas = Number(response?.reservasConservadas ?? response?.ReservasConservadas) || 0;
        if (conservadas > 0) {
          setNotice(conservadas === 1
            ? 'Mientras editabas, un alumno reservó este horario. Su reserva se mantuvo.'
            : `Mientras editabas, ${conservadas} alumnos reservaron este horario. Sus reservas se mantuvieron.`);
        }
      } else {
        await particularesApiService.createDisponibilidad(payload);
      }
      setSelectedDay(new Date(`${form.fecha}T${form.horaInicio}:00`));
      closeModal();
      await loadDisponibilidades();
    } catch (err) {
      setError(err.message || 'No pudimos guardar la disponibilidad.');
      if (isEdit) {
        // Se trae el estado actual y se reaplican los cambios del docente, para que pueda ajustar y volver a guardar.
        const rows = await loadDisponibilidades();
        const fresh = rows?.find((item) => item.id === form.id);
        if (fresh) {
          updateForm({
            alumnoIds: applyAlumnosDiff(fresh.alumnoIds, payload.alumnoIdsOriginales, alumnoIds),
            alumnoIdsOriginales: fresh.alumnoIds
          });
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = modal.form.id;
    if (!id) return;

    setSaving(true);
    setError('');
    try {
      await particularesApiService.deleteDisponibilidad(id);
      closeModal();
      await loadDisponibilidades();
    } catch (err) {
      setError(err.message || 'No pudimos eliminar la disponibilidad.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="particular-calendar-section">
      {error && (
        <Alert
          title="Calendario"
          message={error}
          type="error"
          onClose={() => setError('')}
        />
      )}
      {!error && notice && (
        <Alert
          title="Calendario"
          message={notice}
          type="info"
          onClose={() => setNotice('')}
        />
      )}

      <div className="particular-calendar-toolbar">
        <div>
          <p className="section-kicker">Calendario docente</p>
          <h2>Disponibilidad para clases</h2>
          <span>{particularName}</span>
          <p className="particular-calendar-intro">
            Publicá los horarios en los que podés dar clase. Tus alumnos los ven en su calendario y pueden reservarlos
            hasta completar el cupo. También podés inscribir alumnos vos mismo.
          </p>
          <div className="particular-calendar-legend">
            <span><i className="legend-swatch free" /> Con lugares</span>
            <span><i className="legend-swatch full" /> Cupo completo</span>
          </div>
        </div>
        <div className="particular-calendar-actions">
          {canManageAll && (
            <label className="particular-calendar-filter">
              <span>Profesor</span>
              <select value={selectedProfesorId} onChange={(event) => setSelectedProfesorId(event.target.value)}>
                <option value="">Todos</option>
                {profesorOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="particular-secondary-btn" onClick={loadDisponibilidades} disabled={loading || !range}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            Actualizar
          </button>
          <button type="button" className="particular-primary-btn" onClick={() => openCreateModal()} disabled={!defaultProfesorId}>
            <i className="fas fa-plus" />
            Publicar disponibilidad
          </button>
        </div>
      </div>

      <div className="particular-calendar-layout">
        <section className="particular-calendar-card">
          {loading && (
            <div className="particular-calendar-loading">
              <i className="fas fa-spinner fa-spin" />
              <span>Cargando disponibilidad...</span>
            </div>
          )}
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            locale={esLocale}
            initialView="timeGridWeek"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            buttonText={{
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día'
            }}
            allDaySlot={false}
            nowIndicator
            selectable
            selectMirror
            selectMinDistance={8}
            slotDuration="00:15:00"
            snapDuration="00:15:00"
            slotMinTime="06:00:00"
            slotMaxTime="23:00:00"
            height="auto"
            events={calendarEvents}
            datesSet={handleCalendarDates}
            dateClick={handleDateClick}
            select={handleSelect}
            eventClick={(info) => {
              const raw = info.event.extendedProps.raw;
              if (raw?.ocupado) {
                openDetailModal(raw);
                return;
              }
              openEditModal(raw);
            }}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          />
        </section>

        <aside className="particular-day-detail">
          <div className="particular-day-detail-header">
            <div>
              <span>Detalle del día</span>
              <strong>{selectedDayKey.split('-').reverse().join('/')}</strong>
            </div>
            <button type="button" className="particular-icon-btn action-edit" onClick={() => openCreateModal(selectedDay)} title="Agregar">
              <i className="fas fa-plus" />
            </button>
          </div>

          <div className="particular-day-grid">
            {selectedDayItems.length === 0 ? (
              <div className="particular-day-empty">
                <i className="far fa-calendar" />
                <span>Sin disponibilidad publicada.</span>
              </div>
            ) : selectedDayItems.map((item) => {
              const color = normalizeColor(item.profesorColor);
              const alumnoNames = item.alumnos.map((alumno) => getName(alumno, 'Alumno'));
              return (
                <button
                  type="button"
                  key={item.id}
                  className={`particular-day-slot${item.completo ? ' occupied' : ''}`}
                  style={{
                    '--profesor-color': color,
                    '--profesor-color-soft': hexToRgba(color, 0.08),
                    '--profesor-color-border': hexToRgba(color, 0.28),
                    '--profesor-color-border-hover': hexToRgba(color, 0.46)
                  }}
                  onClick={() => (item.ocupado ? openDetailModal(item) : openEditModal(item))}
                >
                  <span className="slot-time">
                    {formatTime(item.fechaInicioLocal)} - {formatTime(item.fechaFinLocal)}
                  </span>
                  <span className="slot-main">{item.profesorNombre}</span>
                  <span className="slot-meta">{formatDuration(item.duracionMinutos)} · {formatCupo(item)}</span>
                  {alumnoNames.length > 0 && (
                    <span className="slot-students">{alumnoNames.join(', ')}</span>
                  )}
                  {item.jitsiRoomUrl && (
                    <span className="slot-video"><i className="fas fa-video" /> Sala lista</span>
                  )}
                </button>
              );
            })}
          </div>
        </aside>
      </div>

      {modal.open && (
        <Modal
          title={modal.mode === 'edit' ? 'Editar disponibilidad' : 'Publicar disponibilidad'}
          icon="fas fa-calendar-plus"
          onClose={closeModal}
        >
          <form className="particular-calendar-form" onSubmit={handleSubmit}>
            <label>
              Profesor
              <SearchableSelect
                value={modal.form.idUsuarioProfesor}
                onChange={(value) => updateForm({ idUsuarioProfesor: value, alumnoIds: canManageAll ? modal.form.alumnoIds : [] })}
                options={profesorOptions}
                placeholder="Seleccionar profesor"
                emptyText="No hay profesores disponibles."
                disabled={!canManageAll || saving}
              />
            </label>

            <div className="particular-calendar-form-grid">
              <label>
                Fecha
                <input
                  type="date"
                  value={modal.form.fecha}
                  onChange={(event) => updateForm({ fecha: event.target.value })}
                  disabled={saving}
                  required
                />
              </label>
              <label>
                Hora
                <input
                  type="time"
                  value={modal.form.horaInicio}
                  onChange={(event) => updateForm({ horaInicio: event.target.value })}
                  step="900"
                  disabled={saving}
                  required
                />
              </label>
            </div>

            <label>
              Duración
              <select
                value={modal.form.duracionMinutos}
                onChange={(event) => updateForm({ duracionMinutos: Number(event.target.value) })}
                disabled={saving}
              >
                {DURACION_OPTIONS.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {minutes} minutos{minutes >= 60 ? ` (${formatDuration(minutes)})` : ''}
                  </option>
                ))}
              </select>
              <p className="duration-summary">
                Se pondrá a disposición un espacio de <strong>{Number(modal.form.duracionMinutos) || 60} minutos</strong> disponibles.
              </p>
            </label>

            <div className="particular-calendar-cupo">
              <span className="particular-calendar-cupo-label">Cupo de alumnos</span>
              <div className="particular-calendar-cupo-toggle" role="radiogroup" aria-label="Cupo de alumnos">
                <button
                  type="button"
                  role="radio"
                  aria-checked={!modal.form.cupoIlimitado}
                  className={!modal.form.cupoIlimitado ? 'active' : ''}
                  onClick={() => updateForm({ cupoIlimitado: false, cupo: modal.form.cupo || 1 })}
                  disabled={saving}
                >
                  <i className="fas fa-user-friends" />
                  Con cupo
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={modal.form.cupoIlimitado}
                  className={modal.form.cupoIlimitado ? 'active' : ''}
                  onClick={() => updateForm({ cupoIlimitado: true })}
                  disabled={saving}
                >
                  <i className="fas fa-infinity" />
                  Sin límite
                </button>
              </div>
              {!modal.form.cupoIlimitado && (
                <label className="particular-calendar-cupo-input">
                  Lugares
                  <input
                    type="number"
                    min={1}
                    max={CUPO_MAXIMO}
                    step={1}
                    value={modal.form.cupo}
                    onChange={(event) => updateForm({ cupo: event.target.value === '' ? '' : Number(event.target.value) })}
                    disabled={saving}
                    required
                  />
                </label>
              )}
              <p className="duration-summary">
                {modal.form.cupoIlimitado
                  ? 'Cualquier cantidad de alumnos puede reservar este horario.'
                  : Number(modal.form.cupo) === 1
                    ? 'Clase individual: se completa con la primera reserva.'
                    : <>Se completa cuando reservan <strong>{Number(modal.form.cupo) || 0} alumnos</strong>.</>}
              </p>
            </div>

            <label>
              Alumnos inscriptos
              <SearchableSelect
                multiple
                values={modal.form.alumnoIds}
                onChange={(values) => updateForm({ alumnoIds: values })}
                options={alumnoOptions}
                placeholder={loadingAlumnos ? 'Cargando alumnos...' : 'Buscar alumnos'}
                emptyText={loadingAlumnos ? 'Cargando alumnos...' : 'No hay alumnos disponibles.'}
                noResultsText="No hay alumnos que coincidan."
                disabled={saving || loadingAlumnos || !modal.form.idUsuarioProfesor}
              />
              <p className="duration-summary">
                {(() => {
                  const inscriptos = Array.isArray(modal.form.alumnoIds) ? modal.form.alumnoIds.length : 0;
                  if (modal.form.cupoIlimitado) {
                    return inscriptos > 0
                      ? `${inscriptos} inscripto${inscriptos === 1 ? '' : 's'}. El resto puede reservar sin límite.`
                      : 'Opcional: inscribí alumnos vos mismo. Los demás pueden reservar desde su calendario.';
                  }
                  const cupo = Number(modal.form.cupo) || 0;
                  const libres = Math.max(0, cupo - inscriptos);
                  return inscriptos > cupo
                    ? `Hay ${inscriptos} inscriptos y el cupo es ${cupo}.`
                    : `${inscriptos} de ${cupo} lugares ocupados. ${libres === 0 ? 'Cupo completo.' : `Quedan ${libres} para reservar.`}`;
                })()}
              </p>
            </label>

            <label>
              Notas
              <textarea
                value={modal.form.notas}
                onChange={(event) => updateForm({ notas: event.target.value })}
                maxLength={500}
                disabled={saving}
              />
            </label>

            <div className="particular-calendar-modal-actions">
              {modal.mode === 'edit' && (
                <button type="button" className="particular-danger-btn" onClick={() => setConfirmDelete(true)} disabled={saving}>
                  <i className="fas fa-trash" />
                  Eliminar
                </button>
              )}
              <button type="button" className="particular-secondary-btn" onClick={closeModal} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="particular-primary-btn" disabled={saving || !modal.form.idUsuarioProfesor}>
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                Guardar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal.open && confirmDelete && (
        <Modal title="Eliminar disponibilidad" icon="fas fa-trash" onClose={() => !saving && setConfirmDelete(false)}>
          <div className="particular-calendar-confirm">
            <p>
              Se va a eliminar este horario del calendario.
              {modal.form.alumnoIds?.length > 0 && (
                <> Los <strong>{modal.form.alumnoIds.length} alumnos inscriptos</strong> dejarán de verlo en su calendario.</>
              )}
            </p>
            <div className="particular-calendar-modal-actions">
              <button type="button" className="particular-secondary-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="particular-danger-btn" onClick={handleDelete} disabled={saving}>
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-trash'}`} />
                Eliminar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {detailSlot && (
        <Modal
          title="Clase reservada"
          icon="fas fa-video"
          onClose={closeDetailModal}
        >
          <div className="particular-calendar-class-detail">
            <div className="class-detail-hero" style={{ '--profesor-color': detailSlot.profesorColor }}>
              <div>
                <span>{formatTime(detailSlot.fechaInicioLocal)} - {formatTime(detailSlot.fechaFinLocal)}</span>
                <strong>{detailSlot.profesorNombre}</strong>
              </div>
              <span>{formatDuration(detailSlot.duracionMinutos)}</span>
            </div>

            <div className="class-detail-grid">
              <div>
                <span>Fecha</span>
                <strong>{toDateKey(detailSlot.fechaInicioLocal).split('-').reverse().join('/')}</strong>
              </div>
              <div>
                <span>Cupo</span>
                <strong>{formatCupo(detailSlot)}</strong>
              </div>
              <div>
                <span>Estado</span>
                <strong>{detailSlot.completo ? 'Cupo completo' : 'Con lugares'}</strong>
              </div>
            </div>

            <section className="class-detail-section">
              <h4>Alumnos inscriptos</h4>
              {detailSlot.alumnos.length === 0 ? (
                <p>Todavía no hay alumnos inscriptos.</p>
              ) : (
                <div className="class-detail-students">
                  {detailSlot.alumnos.map((alumno) => (
                    <span key={getId(alumno)}>{getName(alumno, 'Alumno')}</span>
                  ))}
                </div>
              )}
            </section>

            {detailSlot.notas && (
              <section className="class-detail-section">
                <h4>Notas</h4>
                <p>{detailSlot.notas}</p>
              </section>
            )}

            <section className="class-detail-section">
              <h4>Videollamada</h4>
              <p>{detailSlot.jitsiRoomUrl ? 'Sala asignada para esta clase.' : 'La sala todavía no está generada.'}</p>
            </section>

            <div className="particular-calendar-modal-actions">
              <button type="button" className="particular-secondary-btn" onClick={() => openEditModal(detailSlot)}>
                <i className="fas fa-pen" />
                Editar
              </button>
              <button type="button" className="particular-secondary-btn" onClick={closeDetailModal}>
                Cerrar
              </button>
              <button
                type="button"
                className="particular-primary-btn"
                onClick={() => openJitsiRoom(detailSlot.jitsiRoomUrl)}
                disabled={!detailSlot.jitsiRoomUrl}
              >
                <i className="fas fa-video" />
                Ingresar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ParticularDisponibilidadCalendar;
